import React, { lazy, Suspense, useCallback, useContext, useEffect, useState } from "react";
import { LanguageContext } from "./LanguageContext";
import "./Home.css";
import { faqs } from "./data/faqs";
import { images } from "./data/images";
import SEO from "../src/components/SEO";

const FAQ = lazy(() => import("./components/FAQ"));
const ImageButtonSections = lazy(() => import("./components/ImageButtonSections"));

const Home = () => {
  const { language } = useContext(LanguageContext);
  const [theme, setTheme] = useState("light");

  const t = useCallback((en, hi) => (language === "english" ? en : hi), [language]);

  const handleShare = useCallback((imageUrl) => {
    const shareTitle = t("Check out this amazing image!", "इस अद्भुत तस्वीर को देखें!");
    const shareText = t("Here is an image you might like!", "यहां एक तस्वीर है जो आपको पसंद आ सकती है!");

    if (navigator.share) {
      navigator.share({ title: shareTitle, text: shareText, url: imageUrl }).catch(console.log);
    } else {
      const url = encodeURIComponent(imageUrl);
      const message = encodeURIComponent(shareTitle);
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${message}`, "_blank");
      window.open(`https://twitter.com/intent/tweet?url=${url}&text=${message}`, "_blank");
      window.open(`https://api.whatsapp.com/send?text=${message} ${url}`, "_blank");
    }
  }, [t]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) setTheme(savedTheme);
  }, []);

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  const meta = {
    title: "Snapseed QR Codes - Free Photo Editing Presets & Filters",
    description: "Discover 500+ must-have Snapseed QR codes for stunning photo edits. Download free presets and filters for DSLR-like results!",
    url: "https://www.snapseedqrcodes.download/",
    image: "https://www.snapseedqrcodes.download/snapseedqrcodes_logo.webp",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Snapseed QR Codes",
      url: "https://www.snapseedqrcodes.download/",
      description: "Discover 500+ must-have Snapseed QR codes for stunning photo edits.",
      publisher: {
        "@type": "Organization",
        name: "Snapseed QR Codes",
        logo: {
          "@type": "ImageObject",
          url: "https://www.snapseedqrcodes.download/snapseedqrcodes_logo.webp",
        },
      },
      datePublished: "2025-01-29",
      dateModified: "2025-05-07",
    },
  };

  return (
    <div className="home-page">
      <SEO {...meta} />

      <button className="theme-toggle-btn" onClick={toggleTheme}>
        {theme === "light" ? "🌙 Dark Mode" : "☀️ Light Mode"}
      </button>

      <section className="hero-section">
        <h1 className="hero-title">
          {t(
            <>55+ Must-Have Snapseed QR Codes: Your Complete Guide & <a href="/collection" className="hero-link">Collection</a></>,
            <>55+ ज़रूरी स्नैपसीड क्यूआर कोड: आपकी पूरी गाइड और <a href="/collection" className="hero-link">संग्रह</a></>
          )}
        </h1>
      </section>

      {/* Snapseed QR Code Overview */}
      <section className="snapseed">
        <h2>{t("Understanding Snapseed QR Codes", "Snapseed QR कोड को समझना")}</h2>
        <p>{t(
          "Snapseed QR codes are a revolutionary method for photo editing. They provide an easy way to store and share your favorite edits.",
          "Snapseed QR कोड्स फोटो एडिटिंग का एक क्रांतिकारी तरीका हैं। ये आपको आपकी पसंदीदा एडिट्स को स्टोर और शेयर करने का आसान विकल्प देते हैं।"
        )}</p>

        <h2>{t("What is a Snapseed QR Code?", "Snapseed QR कोड क्या है?")}</h2>
        <p>{t(
          `Think of a Snapseed QR code like a recipe card that remembers your photo edits. It is a special square pattern that saves the complete method of editing your picture. All your favorite filters, colors, and touch-ups are secured in this simple code. The best part? You can use this code repeatedly. Just scan the QR code with your camera, and Snapseed will apply those beautiful edits to your new photo.`,
          `Snapseed QR कोड को एक रेसिपी कार्ड की तरह समझें जो आपकी फोटो एडिट्स को याद रखता है...`
        )}</p>
      </section>

      <hr />

      {/* Benefits Section */}
      <section className="snapseed">
        <h2>{t("Making the Most of Snapseed QR Codes", "Snapseed QR कोड्स का सर्वश्रेष्ठ उपयोग करना")}</h2>
        <p>{t(
          "By using Snapseed QR codes effectively, you can save both time and effort in your editing process. These codes make the editing process easier and more enjoyable.",
          "Snapseed QR कोड्स का सही उपयोग करके आप अपनी एडिटिंग में समय और मेहनत दोनों बचा सकते हैं।"
        )}</p>

        <h2>{t("Why You'll Love Snapseed QR Codes", "Snapseed QR कोड्स से क्यों प्यार होगा?")}</h2>
        <p>{t(
          `When you edit a lot of pictures, these QR codes become your best friend. They help you save time, allowing you to add your favorite edits with just a scan. Want to share your amazing photo style with friends? Just send them your QR code.`,
          `जब आप बहुत सारी तस्वीरें एडिट करते हैं, तो ये QR कोड्स आपके सबसे अच्छे दोस्त बन जाते हैं...`
        )}</p>
      </section>

      <hr />

      {/* QR Creation Instructions */}
      <section className="snapseed">
        <h2>{t("How to Make These QR Codes", "इन QR कोड्स को कैसे बनाएं?")}</h2>
        <ul className="benefits-list">
          {[
            ["First, make your photo look perfect in Snapseed using any tools you like.", "Snapseed में अपनी तस्वीर को किसी भी टूल का उपयोग करके परफेक्ट बनाएं।"],
            ["Tap Edit Stacks at the top right corner and look for 'QR Look'.", "टॉप राइट कॉर्नर पर 'Edit Stacks' टैप करें और 'QR Look' खोजें।"],
            ["Click 'Create QR Look' to make your special code.", "'Create QR Look' पर क्लिक करके अपना विशेष कोड बनाएं।"],
            ["Save this code on your phone or send it to friends", "इस कोड को अपने फोन में सेव करें या दोस्तों को भेजें।"],
            ["When you want to use it again, pick a new photo", "जब आप इसे फिर से उपयोग करना चाहें, तो एक नई तस्वीर चुनें।"],
            ["Go to QR Look, then Scan QR Look", "QR Look में जाएं, फिर Scan QR Look पर क्लिक करें।"],
            ["Point your camera at the code, and your new photo gets the same beautiful edits", "अपने कैमरे को कोड पर पॉइंट करें, और आपकी नई तस्वीर को वही सुंदर एडिट्स मिलेंगे।"],
          ].map(([en, hi], i) => <li key={i}>{t(en, hi)}</li>)}
        </ul>
      </section>

      <hr />

      {/* Preset Promotion Section */}
      <section className="snapseed">
        <h2>{t("55+ Top Snapseed QR Code Looks and Presets", "55+ Snapseed QR कोड लुक्स और प्रीसेट्स")}</h2>
        <p>
          {language === "english" ? (
            <>Now for the fun part! We're sharing more than 55+ great Snapseed QR looks and presets. <a href="/collection">Check out our presets collection here.</a></>
          ) : (
            <>अब बारी है सबसे मजेदार हिस्से की! यहां हम 60+ अद्भुत Snapseed QR लुक्स और प्रीसेट्स साझा कर रहे हैं। <a href="/collection">यहां हमारे प्रीसेट्स संग्रह को देखें।</a></>
          )}
        </p>
        <p>
          {t(
            "Think of this section like a cookbook for photo editing. Each QR code is like a recipe that gives your photos a unique touch. Whether you want your photos to look dreamy, sharp, vintage, or modern, we have the right code for you!",
            "इस सेक्शन को अपनी फोटो एडिटिंग कुकबुक की तरह समझें... हर QR कोड एक रेसिपी की तरह है।"
          )}
        </p>
      </section>

      <hr />

      <section className="snapseed">
        <Suspense fallback={<div>Loading images...</div>}>
          {images.map((image, index) => (
            <ImageButtonSections
              key={index}
              id={index + 1}
              image={image.src}
              altText={image.alt ? t(image.alt.en, image.alt.hi) : "Snapseed QR code preset"}
              heading={t(image.heading.en, image.heading.hi)}
              description={t(image.description.en, image.description.hi)}
              onShare={() => handleShare(image.src)}
            />
          ))}
        </Suspense>
      </section>

      <hr />

      <Suspense fallback={<div>Loading FAQs...</div>}>
        <FAQ faqs={faqs} />
      </Suspense>
    </div>
  );
};

export default Home;
